import React, { useState, useEffect } from "react";
import {
  fetchUserOrder,
  fetchVoucherDetails,
} from "../../../apiCalls/urlFunction";
import VoucherView from "../../../components/alertPopup/VoucherView";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  Avatar,
  Button,
  Container,
  Paper,
  useTheme,
  alpha,
  Chip,
  CircularProgress,
} from "@mui/material";
import { HandCoins } from "lucide-react";
import { motion } from "framer-motion";
import {
  LocalOffer,
  TrendingUp,
  CardGiftcard,
  ShoppingBag,
  Star,
  AccountBalanceWalletOutlined,
  LocalOfferOutlined,
} from "@mui/icons-material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import { useNavigate } from "react-router-dom";
import { dateChanger } from "../../../shared/extra";
import { Stack } from "@mui/system";
import CursorPagination from "../../../components/pagination/CursorPagination";
import { capitalizeFirst } from "../../../hooks/utilsFn/utilsFn";

const EmptyOrdersScreen = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: [0.4, 0, 0.2, 1],
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const floatAnimation = {
    y: [0, -8, 0],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  const pulseGlow = {
    scale: [1, 1.08, 1],
    opacity: [0.6, 1, 0.6],
    transition: {
      duration: 2.5,
      repeat: Infinity,
      ease: "easeInOut",
    },
  };

  // Featured categories
  const categories = [
    {
      icon: <LocalOffer sx={{ fontSize: 20 }} />,
      label: "Gift Cards",
      color: "#e9b367",
      onClick: () => navigate("/card-list"),
    },
    {
      icon: <Star sx={{ fontSize: 20 }} />,
      label: "Popular",
      color: "#667eea",
      onClick: () => navigate("/card-list"),
    },
    {
      icon: <TrendingUp sx={{ fontSize: 20 }} />,
      label: "Trending",
      color: "#f093fb",
      onClick: () => navigate("/card-list"),
    },
  ];

  return (
    <Container
      maxWidth="xs"
      disableGutters
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 3,
        background: `linear-gradient(180deg, ${alpha(theme.palette.background.default, 0.5)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
      }}
    >
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        style={{
          width: "100%",
          maxWidth: "420px",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: "blur(20px)",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.06)}`,
            boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative Gradient Orbs */}
          <Box
            sx={{
              position: "absolute",
              top: -80,
              right: -80,
              width: 200,
              height: 200,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha("#e9b367", 0.08)} 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -60,
              left: -60,
              width: 180,
              height: 180,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.06)} 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />

          {/* Gift Icon with Elegant Design */}
          <motion.div
            variants={itemVariants}
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 28,
            }}
          >
            <Box sx={{ position: "relative" }}>
              {/* Glow Effect */}
              <motion.div
                animate={pulseGlow}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  background: `radial-gradient(circle, ${alpha("#e9b367", 0.15)} 0%, transparent 70%)`,
                }}
              />

              {/* Main Icon Container */}
              <motion.div
                animate={floatAnimation}
                style={{
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <Box
                  sx={{
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg, ${alpha("#e9b367", 0.12)} 0%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `2px solid ${alpha("#e9b367", 0.15)}`,
                    position: "relative",
                  }}
                >
                  <CardGiftcard
                    sx={{
                      fontSize: 48,
                      color: "#e9b367",
                    }}
                  />

                  {/* Decorative Ring */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -4,
                      width: 16,
                      height: 16,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, #e9b367 0%, ${theme.palette.primary.main} 100%)`,
                      opacity: 0.6,
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -4,
                      left: -4,
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 100%)`,
                      opacity: 0.4,
                    }}
                  />
                </Box>
              </motion.div>
            </Box>
          </motion.div>

          {/* Title with Gradient */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontFamily: "Onest, Be Vietnam Pro, sans-serif",
                fontWeight: 700,
                fontSize: { xs: "1.6rem", sm: "1.8rem" },
                mb: 1.5,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 60%, ${theme.palette.primary.main} 100%)`,
                backgroundSize: "200% 200%",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              No Orders Yet
            </Typography>
          </motion.div>

          {/* Description */}
          <motion.div variants={itemVariants}>
            <Typography
              variant="body1"
              align="center"
              sx={{
                fontFamily: "Be Vietnam Pro, sans-serif",
                fontSize: { xs: "0.95rem", sm: "1rem" },
                color: alpha(theme.palette.text.secondary, 0.8),
                mb: 2,
                lineHeight: 1.7,
                maxWidth: "320px",
                mx: "auto",
              }}
            >
              Discover the perfect gift for your loved ones with our curated
              collection of premium gift vouchers.
            </Typography>
          </motion.div>

          {/* Main CTA Button */}
          <motion.div
            variants={itemVariants}
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
          >
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/card-list")}
              sx={{
                py: { xs: 1.5, sm: 1.75 },
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, #e9b367 50%, ${theme.palette.primary.main} 100%)`,
                backgroundSize: "200% 200%",
                animation: "gradientMove 3s ease infinite",
                boxShadow: "0 8px 30px rgba(233, 179, 103, 0.25)",
                fontFamily: "Onest, Be Vietnam Pro, sans-serif",
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 600,
                textTransform: "none",
                position: "relative",
                overflow: "hidden",
                "&:hover": {
                  boxShadow: "0 12px 40px rgba(233, 179, 103, 0.35)",
                  transform: "translateY(-2px)",
                },
                "&::after": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: "-100%",
                  width: "100%",
                  height: "100%",
                  background:
                    "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
                  transition: "all 0.8s ease",
                },
                "&:hover::after": {
                  left: "100%",
                },
              }}
              startIcon={<ShoppingBag sx={{ fontSize: 22 }} />}
            >
              Explore Gift Vouchers
            </Button>
          </motion.div>

          {/* Subtle Decorative Line */}
          <motion.div
            variants={itemVariants}
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 2,
                borderRadius: 2,
                background: `linear-gradient(90deg, transparent, ${alpha("#e9b367", 0.3)}, transparent)`,
              }}
            />
          </motion.div>
        </Paper>
      </motion.div>

      {/* CSS Animation */}
      <style>{`
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </Container>
  );
};

// FIXED: Properly flatten order data to create separate entries for each denomination
const flattenOrderDenominations = (data) => {
  if (!data?.length) return [];

  let finalData = [];

  data?.forEach((item) => {
    const denominations = item?.orderDenominations || [];

    if (denominations.length > 1) {
      // Multiple denominations - create a separate entry for each
      denominations.forEach((denomination, index) => {
        const newItem = {
          ...item,
          // Create a unique ID for each denomination entry
          id: `${item.id}-${denomination.id || index}`,
          // Override with denomination-specific data
          amount: Number(
            denomination?.totalGrossAmount ||
            (denomination?.grossItemAmount * denomination?.quantity) || 0
          ).toFixed(2),
          youpay: Number(
            denomination?.totalNetAmount ||
            (denomination?.netItemAmount * denomination?.quantity) || 0
          ).toFixed(2),
          quantity: denomination?.quantity || 0,
          discount: denomination?.cashbackOrDiscount === "DISCOUNT"
            ? `${Number(denomination?.discountValue || 0).toFixed(2)} AED`
            : "-",
          cashback: denomination?.cashbackOrDiscount === "CASHBACK"
            ? `${Number(denomination?.discountValue || 0).toFixed(2)} AED`
            : "-",
          // Store denomination data
          customOrderDenominations: denomination,
          denominationIndex: index,
          isMultiDenom: true,
          denominationId: denomination?.id,
          // Store the total count for reference
          totalDenominations: denominations.length,
          // Keep original order data for reference
          originalOrder: item,
          // Store original order ID for reference
          originalOrderId: item.id,
        };
        finalData.push(newItem);
      });
    } else {
      // Single denomination - keep as is
      const singleDenom = denominations[0] || {};
      const newItem = {
        ...item,
        id: item.id,
        amount: Number(
          singleDenom?.totalGrossAmount ||
          (singleDenom?.grossItemAmount * singleDenom?.quantity) || 0
        ).toFixed(2),
        youpay: Number(
          singleDenom?.totalNetAmount ||
          (singleDenom?.netItemAmount * singleDenom?.quantity) || 0
        ).toFixed(2),
        quantity: singleDenom?.quantity || 0,
        discount: singleDenom?.cashbackOrDiscount === "DISCOUNT"
          ? `${Number(singleDenom?.discountValue || 0).toFixed(2)} AED`
          : "-",
        cashback: singleDenom?.cashbackOrDiscount === "CASHBACK"
          ? `${Number(singleDenom?.discountValue || 0).toFixed(2)} AED`
          : "-",
        customOrderDenominations: singleDenom,
        denominationIndex: 0,
        isMultiDenom: false,
        denominationId: singleDenom?.id,
        totalDenominations: 1,
        originalOrder: item,
        originalOrderId: item.id,
      };
      finalData.push(newItem);
    }
  });

  return finalData;
};

export function MobileOrder() {
  const [OrderData, setOrderData] = useState([]);
  const [Loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("ALL");
  const [vouchers, setVouchers] = useState({
    ProductData: {},
    vouchers: [],
    showVoucher: false,
    loading: false,
    selectedDenomination: null,
  });

  const fontFamily = "Be Vietnam Pro";

  const responseMapper = (res) => {
    const edges = res?.data?.edges || [];

    return {
      data: edges.map((e) => e?.node),
      totalCount: res?.data?.totalCount || 0,
      endCursor: res?.data?.pageInfo?.endCursor,
    };
  };

  const fetchVoucherDetail = async (item, denominationId = null) => {
    setVouchers((prev) => ({
      ...prev,
      loading: true,
    }));

    try {
      // Use originalOrderId if available, otherwise use item.id
      const orderId = item?.originalOrderId || item?.id;
      const voucherResponse = await fetchVoucherDetails(orderId);

      if (voucherResponse?.status) {
        let filteredDenominations = voucherResponse?.data?.orderDenominations || [];
        let selectedDenomination = null;

        // If a specific denomination ID is provided, filter for it
        if (denominationId && filteredDenominations.length > 1) {
          selectedDenomination = filteredDenominations.find(
            d => d.id === denominationId || d.denominationId === denominationId
          );

          if (selectedDenomination) {
            filteredDenominations = [selectedDenomination];
          }
        }

        setVouchers({
          ProductData: { ...voucherResponse?.data?.productDatum, item } || {},
          vouchers: voucherResponse?.data?.orderVouchers || [],
          showVoucher: true,
          loading: false,
          selectedDenomination: selectedDenomination || null,
          allDenominations: voucherResponse?.data?.orderDenominations || [],
          isMultiDenomView: filteredDenominations.length > 1 && !!denominationId,
          amount: filteredDenominations,
        });
      } else {
        setVouchers((prev) => ({
          ...prev,
          loading: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching voucher details:", error);
      setVouchers((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  // Flatten the order data when it changes
  const flattenedOrders = React.useMemo(() => {
    return flattenOrderDenominations(OrderData);
  }, [OrderData]);

  // Filter the flattened data
  const filteredData = React.useMemo(() => {
    return filterType === "ALL"
      ? flattenedOrders
      : flattenedOrders?.filter((item) => item?.status?.toUpperCase() === filterType);
  }, [flattenedOrders, filterType]);

  if (!Loading && OrderData?.length === 0) return <EmptyOrdersScreen />;

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: 1,
          width: "100%",
          mb: 2,
        }}
      >
        <ShoppingBagIcon
          sx={{
            color: "primary.main",
            fontSize: 25,
            cursor: "pointer",
            "&:hover": {
              color: "secondary.main",
            },
          }}
        />
        <Typography
          sx={{
            fontFamily: fontFamily,
            fontSize: "18px",
            color: "#000",
          }}
        >
          My Orders
        </Typography>
      </Box>

      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            width: "100%",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          {["ALL", "COMPLETED", "FAILED", "PROCESSING"].map((type) => {
            const isSelected = filterType === type;

            return (
              <Chip
                key={type}
                label={type}
                onClick={() => setFilterType(type)}
                size="small"
                sx={{
                  flex: 1,
                  cursor: "pointer",
                  fontWeight: 700,
                  fontFamily: fontFamily,
                  fontSize: "8.5px",
                  height: "31px",
                  textTransform: "capitalize",
                  bgcolor: isSelected ? "#F3F0FF" : "#FFFFFF",
                  color: isSelected ? "#4F35CD" : "#000",
                  border: isSelected
                    ? "1px solid #4F35CD"
                    : "1px solid #E2E8F0",
                  "& .MuiChip-label": {
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  },
                }}
              />
            );
          })}
        </Stack>
      </Box>

      {Loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
          <Typography sx={{ mt: 2, fontSize: "15px", fontFamily: fontFamily }}>
            Loading...
          </Typography>
        </Box>
      ) : filteredData?.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ fontFamily: fontFamily, color: "text.secondary" }}>
            No orders found
          </Typography>
        </Box>
      ) : (
        filteredData?.map((item, index) => (
          <Card
            key={`${item?.id}-${item?.denominationIndex || 0}-${index}`}
            sx={{
              width: "100%",
              borderRadius: 4,
              boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
              bgcolor: "#ffffff",
              mb: 2,
              position: "relative",
            }}
          >
            <CardContent sx={{ "&:last-child": { pb: 3 } }}>
              {/* Header Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box
                    component="img"
                    src={item?.productDatum?.productImages?.[0]?.url}
                    sx={{
                      width: 64,
                      height: 49,
                      borderRadius: 2,
                      objectFit: "cover",
                      flexShrink: 0,
                    }}
                  />

                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "13px",
                          color: "primary.main",
                          fontFamily: fontFamily,
                          textTransform: "capitalize",
                        }}
                      >
                        {item?.productDatum?.name}
                      </Typography>

                      <Box
                        sx={{
                          bgcolor: item?.status?.toUpperCase() === "COMPLETED" ? "#E8F5E9" : "#FFF3E0",
                          color: item?.status?.toUpperCase() === "COMPLETED" ? "#02b847" : "#F57C00",
                          fontSize: "8px",
                          fontWeight: 700,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: 1,
                          letterSpacing: "0.5px",
                          fontFamily: fontFamily,
                          lineHeight: 1,
                          width: "fit-content",
                        }}
                      >
                        {item?.status}
                      </Box>
                    </Box>

                    <Typography
                      sx={{
                        color: "dark.main",
                        fontSize: "10px",
                        mt: 0.75,
                        fontWeight: 500,
                        fontFamily: fontFamily,
                      }}
                    >
                      ID: {item?.referenceId}
                    </Typography>
                  </Box>
                </Box>

                {/* Right side Total & Paid Badge */}
                <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                  <Typography
                    sx={{
                      fontWeight: 500,
                      fontSize: "13px",
                      color: "primary.main",
                      fontFamily: fontFamily,
                      lineHeight: 1.2,
                    }}
                  >
                    AED {item?.amount}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.75,
                    }}
                  >
                    <Box
                      sx={{
                        bgcolor: "#FFF8E1",
                        color: "#F57F17",
                        fontSize: "8px",
                        fontWeight: 700,
                        px: 1.2,
                        py: 0.4,
                        borderRadius: 1,
                        letterSpacing: "0.5px",
                        fontFamily: fontFamily,
                        textTransform: "uppercase",
                      }}
                    >
                      {item?.paymentStatus}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Dashed Separator */}
              <Divider
                sx={{
                  borderStyle: "dashed",
                  my: 2.5,
                  borderColor: "#CBD5E1",
                  borderWidth: "1px",
                }}
              />

              {/* Payment Details Section */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {/* Payment Method */}
                <Grid
                  item
                  xs={4}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      color: "#FFF",
                      width: 35,
                      height: 35,
                    }}
                  >
                    <AccountBalanceWalletOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "11px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Method
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "10px",
                        color: "primary.main",
                        mt: 0.25,
                        fontFamily: fontFamily,
                        textTransform: "capitalize",
                      }}
                    >
                      {item?.paymentMethod?.toLowerCase() || "—"}
                    </Typography>
                  </Box>
                </Grid>

                {/* Currency */}
                <Grid
                  item
                  xs={4}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      color: "#FFF",
                      width: 35,
                      height: 35,
                    }}
                  >
                    <HandCoins
                      style={{
                        width: 18,
                        height: 18,
                      }}
                    />
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "11px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Currency
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "10px",
                        color: "primary.main",
                        mt: 0.25,
                        fontFamily: fontFamily,
                      }}
                    >
                      AED
                    </Typography>
                  </Box>
                </Grid>

                {/* Discount/Cashback */}
                <Grid
                  item
                  xs={4}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      color: "#FFF",
                      width: 35,
                      height: 35,
                    }}
                  >
                    <LocalOfferOutlined fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontSize: "11px",
                        fontFamily: fontFamily,
                      }}
                    >
                      {capitalizeFirst(item?.customOrderDenominations?.cashbackOrDiscount || 'Discount')}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "10px",
                        color: "primary.main",
                        mt: 0.25,
                        fontFamily: fontFamily,
                      }}
                    >
                      AED {Number(item?.customOrderDenominations?.discountValue || 0).toFixed(2)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Amount Box Section */}
              <Box
                sx={{
                  bgcolor: "#f4f8ff",
                  borderRadius: 3,
                  p: { xs: 1.5, sm: 2 },
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                  width: "100%",
                  boxSizing: "border-box",
                  mb: 2.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      flex: "1 1 25%",
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "11px",
                        color: "dark.main",
                        fontFamily: fontFamily,
                        textTransform: "capitalize",
                      }}
                    >
                      Qty :
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "12px",
                        color: "primary.main",
                        fontFamily: fontFamily,
                      }}
                    >
                      {item?.quantity || 0}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      flex: "1 1 35%",
                      minWidth: 0,
                      textAlign: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "dark.main",
                        fontSize: "11px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Net Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "11px", sm: "13px" },
                        color: "primary.main",
                        fontFamily: fontFamily,
                        whiteSpace: "nowrap",
                      }}
                    >
                      AED {item?.youpay}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      flex: "1 1 35%",
                      minWidth: 0,
                      textAlign: "right",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 600,
                        color: "dark.main",
                        fontSize: "11px",
                        fontFamily: fontFamily,
                      }}
                    >
                      Gross Amount
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "11px", sm: "13px" },
                        color: "primary.main",
                        fontFamily: fontFamily,
                        whiteSpace: "nowrap",
                      }}
                    >
                      AED {item?.amount}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Center Dynamic Voucher Button Container */}
              {item?.status?.toUpperCase() === "COMPLETED" && (
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 4,
                    mb: 2,
                    mx: -3,
                  }}
                >
                  <Divider
                    sx={{
                      width: "100%",
                      borderStyle: "dashed",
                      borderColor: "#CBD5E1",
                      borderWidth: "1.5px",
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={() => fetchVoucherDetail(
                      item,
                      item?.isMultiDenom ? item?.denominationId : null
                    )}
                    disabled={vouchers.loading}
                    sx={{
                      position: "absolute",
                      px: 4,
                      py: 0.8,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
                      bgcolor: "primary.main",
                      fontFamily: fontFamily,
                      "&:hover": {
                        bgcolor: "primary.dark",
                      },
                    }}
                  >
                    View Voucher
                  </Button>
                </Box>
              )}

              {/* Modal / Overlay component */}
              <VoucherView
                voucherData={vouchers}
                onClose={() =>
                  setVouchers({
                    ...vouchers,
                    showVoucher: false,
                  })
                }
              />
            </CardContent>
          </Card>
        ))
      )}

      <CursorPagination
        fetchFunction={fetchUserOrder}
        basePayload={{}}
        limit={6}
        dependency={[filterType]}
        onLoadingChange={setLoading}
        onDataChange={setOrderData}
        responseMapper={responseMapper}
      />
    </Box>
  );
}